import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Form, Input, Select, Card, Badge, Tooltip } from 'antd';
import { CustomerServiceOutlined, CloseOutlined, SendOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { useFetchUserData } from 'queries/auth/user';
import apiRequest from 'helpers/apiRequest';
import './index.scss';

const { TextArea } = Input;

const SupportWidget: React.FC = () => {
  const { translate } = useLanguage();
  const { userData } = useFetchUserData();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    return localStorage.getItem('fuko_support_widget_dismissed') === 'true';
  });
  const [form] = Form.useForm();

  // Detect POS or sales routes where bottom-right is reserved for Checkout button
  const isPosPage = location.pathname.includes('/pos') || location.pathname.includes('/sales') || location.pathname.includes('/cart');

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissed(true);
    localStorage.setItem('fuko_support_widget_dismissed', 'true');
  };

  if (isDismissed) {
    return null;
  }

  const onFinish = async (values: { subject: string; message: string }) => {
    setLoading(true);
    const userName = userData?.name || userData?.username || 'User';
    const shopName = userData?.shop?.name || 'Fuko Customer';
    const userEmail = userData?.email || 'support@fukkoni.com';
    
    const messageBody = `Support Request details:
------------------------------------------
Sender Name: ${userName}
Shop Name: ${shopName}
Topic: ${values.subject}
------------------------------------------

Message Details:
${values.message}

-- Sent via FUKKONI In-App Support Widget --`;

    try {
      await apiRequest({
        url: 'contact',
        method: 'POST',
        payload: {
          name: `${userName} (${shopName})`,
          email: userEmail,
          message: messageBody,
        },
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit support request via API, falling back to mailto:', error);
      const supportEmail = 'nankim45@gmail.com';
      const emailSubject = `[Fuko Support] ${values.subject}`;
      const mailtoUrl = `mailto:${supportEmail}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(messageBody)}`;
      window.location.href = mailtoUrl;
      setIsSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSubmitted(false);
    form.resetFields();
  };

  return (
    <div className={`support-widget-container ${isPosPage ? 'pos-page' : ''}`}>
      {/* Floating Toggle Button with Dismiss Badge */}
      <div className="toggle-btn-wrapper">
        <button 
          className={`support-toggle-btn ${isOpen ? 'active' : ''}`} 
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Open support chat"
        >
          {isOpen ? <CloseOutlined /> : <CustomerServiceOutlined />}
        </button>

        {!isOpen && (
          <Tooltip title={translate('Hide support button')}>
            <button 
              className="support-dismiss-badge" 
              onClick={handleDismiss}
              aria-label="Dismiss support widget"
            >
              <CloseOutlined />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Floating Form Panel */}
      {isOpen && (
        <Card 
          className="support-form-card"
          title={
            <div className="support-header">
              <span className="title-text">{translate('Customer Support')}</span>
              <span className="active-status">
                <Badge status="processing" color="#10b981" /> {translate('Online')}
              </span>
            </div>
          }
          extra={
            <Button 
              type="text" 
              icon={<CloseOutlined style={{ color: '#94a3b8' }} />} 
              onClick={handleClose} 
            />
          }
        >
          {!isSubmitted ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
            >
              <div className="welcome-message">
                {translate("Hi! Have a question or facing an issue? Send us a message directly and we'll help you get it resolved.")}
              </div>

              <Form.Item
                name="subject"
                label={translate('Topic')}
                rules={[{ required: true, message: translate('Please select a topic') }]}
              >
                <Select 
                  placeholder={translate('Choose issue topic')}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  options={[
                    { value: 'Technical Issue', label: translate('Technical Issue') },
                    { value: 'Billing / Subscription', label: translate('Billing / Subscription') },
                    { value: 'Feature Request', label: translate('Feature Request') },
                    { value: 'General Inquiries', label: translate('General Inquiries') },
                    { value: 'Other', label: translate('Other') }
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="message"
                label={translate('Message')}
                rules={[{ required: true, message: translate('Please enter your message') }]}
              >
                <TextArea 
                  rows={3} 
                  placeholder={translate('Describe your question or issue...')} 
                  maxLength={1000}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SendOutlined />}
                  block
                  loading={loading}
                  style={{ background: 'var(--primary-color, #046a38)', borderColor: 'var(--primary-color, #046a38)' }}
                >
                  {translate('Send Message')}
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <div className="success-screen">
              <CheckCircleOutlined className="success-icon" style={{ color: 'var(--primary-color, #10b981)' }} />
              <h3>{translate('Message Sent')}</h3>
              <p>
                {translate('Your support request has been submitted successfully. Our team will get back to you shortly.')}
              </p>
              <Button type="default" onClick={handleClose} block>
                {translate('Close Window')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default SupportWidget;
